import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { School, Truck, X, CalendarClock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import API from '@/config/api';

const AREA_LABELS = {
  children: 'Children list, records & DCF forms',
  attendance: 'Attendance & check-in',
  daily_reports: 'Daily reports',
  incident_reports: 'Incident reports',
  lesson_plans: 'Lesson plans',
};

const ToggleRow = ({ label, hint, checked, onChange, testId }) => (
  <label className="flex items-center justify-between gap-4 py-2">
    <span><span className="font-medium">{label}</span>{hint && <span className="block text-xs text-muted-foreground">{hint}</span>}</span>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </label>
);

const PageChips = ({ options, selected, onToggle, testPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {Object.entries(options).map(([href, label]) => (
      <Button key={href} size="sm" variant={selected.includes(href) ? 'default' : 'outline'} className="rounded-full h-8"
        onClick={() => onToggle(href)} data-testid={`${testPrefix}-${href.replace(/\//g, '-').replace(/^-/, '')}`}>{label}</Button>
    ))}
  </div>
);

const toggle = (list, x) => (list.includes(x) ? list.filter((y) => y !== x) : [...list, x]);

const TeacherPages = ({ t, options, centerPages, onSave }) => {
  const custom = Array.isArray(t.extra_pages);
  const base = { view_all_classrooms: t.view_all_classrooms, assigned_classrooms: t.assigned_classrooms };
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm">
        <Switch checked={custom} data-testid={`teacher-custom-pages-${t.id}`}
          onCheckedChange={(v) => onSave(t, v ? { ...base, extra_pages: centerPages } : { ...base, use_center_pages: true })} />
        {custom ? 'Custom extra pages for this teacher' : 'Uses the center\'s extra pages'}
      </label>
      {custom && <PageChips options={options} selected={t.extra_pages} testPrefix={`teacher-page-${t.id}`}
        onToggle={(href) => onSave(t, { ...base, extra_pages: toggle(t.extra_pages, href) })} />}
    </div>
  );
};

const TeacherRow = ({ t, rooms, options, centerPages, onSave, onTemp, onRemoveTemp }) => {
  const [temp, setTemp] = useState('');
  const toggleRoom = (r) => {
    const next = t.assigned_classrooms.includes(r) ? t.assigned_classrooms.filter((x) => x !== r) : [...t.assigned_classrooms, r];
    onSave(t, { view_all_classrooms: t.view_all_classrooms, assigned_classrooms: next });
  };
  return (
    <div className="py-4 border-b last:border-0 space-y-3" data-testid={`teacher-access-${t.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="font-semibold">{t.name}</p><p className="text-xs text-muted-foreground">{t.position || 'Staff'}</p></div>
        <label className="flex items-center gap-2 text-sm">
          Can see all classrooms
          <Switch checked={t.view_all_classrooms} onCheckedChange={(v) => onSave(t, { view_all_classrooms: v, assigned_classrooms: t.assigned_classrooms })} data-testid={`teacher-view-all-${t.id}`} />
        </label>
      </div>
      {!t.view_all_classrooms && (
        <div className="flex flex-wrap gap-2">
          {rooms.map((r) => (
            <Button key={r} size="sm" variant={t.assigned_classrooms.includes(r) ? 'default' : 'outline'} className="rounded-full h-8"
              onClick={() => toggleRoom(r)} data-testid={`teacher-room-${t.id}-${r.replace(/\s+/g, '-').toLowerCase()}`}>{r}</Button>
          ))}
        </div>
      )}
      {!t.view_all_classrooms && (
        <div className="flex flex-wrap items-center gap-2">
          <CalendarClock className="w-4 h-4 text-muted-foreground" />
          {t.temp_today.map((r) => (
            <Badge key={r} variant="secondary" className="gap-1" data-testid={`teacher-temp-${t.id}-${r.replace(/\s+/g, '-').toLowerCase()}`}>
              {r} (today)<button onClick={() => onRemoveTemp(t, r)} aria-label={`Remove ${r}`}><X className="w-3 h-3" /></button>
            </Badge>
          ))}
          <Select value={temp} onValueChange={setTemp}>
            <SelectTrigger className="w-44 h-8" data-testid={`teacher-temp-select-${t.id}`}><SelectValue placeholder="Cover a room today" /></SelectTrigger>
            <SelectContent>{rooms.filter((r) => !t.assigned_classrooms.includes(r)).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-8" disabled={!temp} onClick={() => { onTemp(t, temp); setTemp(''); }} data-testid={`teacher-temp-add-${t.id}`}>Add for today</Button>
        </div>
      )}
      <TeacherPages t={t} options={options} centerPages={centerPages} onSave={onSave} />
    </div>
  );
};

export const StaffAccessPanel = () => {
  const { getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);

  const load = useCallback(async () => {
    const h = getAuthHeaders();
    const [s, t, c] = await Promise.all([axios.get(`${API}/staff-access/settings`, h), axios.get(`${API}/staff-access/teachers`, h), axios.get(`${API}/classrooms`, h)]);
    setSettings(s.data); setTeachers(t.data); setRooms((c.data || []).map((r) => r.name).filter(Boolean));
  }, [getAuthHeaders]);
  useEffect(() => { load().catch(() => toast.error('Could not load staff access')); }, [load]);

  const saveSettings = async (patch) => {
    try { setSettings((await axios.put(`${API}/staff-access/settings`, patch, getAuthHeaders())).data); toast.success('Access updated'); }
    catch { toast.error('Could not save'); }
  };
  const replace = (row) => setTeachers((ts) => ts.map((x) => (x.id === row.id ? row : x)));
  const call = async (fn) => { try { replace((await fn()).data); } catch { toast.error('Could not update teacher'); } };
  const h = getAuthHeaders();

  if (!settings) return null;
  const areas = settings.classroom_scope_areas;
  return (
    <div className="space-y-6 mb-6" data-testid="staff-access-panel">
      <Card className="card-clay">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><School className="w-5 h-5 text-primary" />Classroom Access</CardTitle>
          <CardDescription>Teachers only see children in their assigned classrooms in the areas you turn on below.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-xl bg-muted/50 p-4" data-testid="classroom-scope-areas">
            <p className="text-sm font-semibold mb-1">Limit teachers to their classrooms in:</p>
            {Object.entries(AREA_LABELS).map(([k, label]) => (
              <ToggleRow key={k} label={label} checked={!!areas[k]} testId={`scope-area-${k}`}
                onChange={(v) => saveSettings({ classroom_scope_areas: { ...areas, [k]: v } })} />
            ))}
          </div>
          <div className="rounded-xl bg-muted/50 p-4 space-y-2" data-testid="teacher-extra-pages">
            <p className="text-sm font-semibold">Teacher menu</p>
            <p className="text-xs text-muted-foreground">Teachers always get Dashboard, Children, Attendance, Daily Reports, Learning, Time Clock and Messages. Turn on extra pages for all teachers:</p>
            <PageChips options={settings.extra_page_options} selected={settings.teacher_extra_pages} testPrefix="center-page"
              onToggle={(href) => saveSettings({ teacher_extra_pages: toggle(settings.teacher_extra_pages, href) })} />
          </div>
          <div data-testid="teacher-access-list">
            {teachers.map((t) => (
              <TeacherRow key={t.id} t={t} rooms={rooms} options={settings.extra_page_options} centerPages={settings.teacher_extra_pages}
                onSave={(row, body) => call(() => axios.put(`${API}/staff-access/teachers/${row.id}`, body, h))}
                onTemp={(row, classroom) => call(() => axios.post(`${API}/staff-access/teachers/${row.id}/temp`, { classroom }, h))}
                onRemoveTemp={(row, classroom) => call(() => axios.delete(`${API}/staff-access/teachers/${row.id}/temp/${encodeURIComponent(classroom)}`, h))} />
            ))}
            {!teachers.length && <p className="text-sm text-muted-foreground">No teachers yet.</p>}
          </div>
        </CardContent>
      </Card>
      <Card className="card-clay">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><Truck className="w-5 h-5 text-primary" />Driver Access</CardTitle>
          <CardDescription>Drivers can always use the Driver App. Everything else is blocked unless you allow it.</CardDescription>
        </CardHeader>
        <CardContent>
          <ToggleRow label="Time Clock" hint="Let drivers clock in and out" checked={settings.driver_allow_timeclock}
            onChange={(v) => saveSettings({ driver_allow_timeclock: v })} testId="driver-allow-timeclock" />
          <ToggleRow label="Messages" hint="Let drivers message the center's admins" checked={settings.driver_allow_messages}
            onChange={(v) => saveSettings({ driver_allow_messages: v })} testId="driver-allow-messages" />
        </CardContent>
      </Card>
    </div>
  );
};
